import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FileText, ShieldCheck, Scale, CheckCircle2 } from "lucide-react";

export default function Legal() {
  const { pathname } = useLocation();

  // Scroll to top on path change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Determine which page content to render based on path
  const getPageData = () => {
    switch (pathname) {
      case "/terms-of-service":
        return {
          title: "Terms of Service",
          effectiveDate: "November 12, 2025",
          subtitle: "Standard B2B Agreement of Precision Home Leads LLC",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>Welcome to Precision Home Leads LLC. By accessing our lead generation or digital marketing platform, purchasing homeowner inquiries, or commissioning advertising campaigns, you (the &quot;Contractor&quot; or &quot;Client&quot;) agree to comply with and be bound by the following Terms of Service.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Scope of B2B Commercial Services</h3>
              <p>Precision Home Leads LLC provides professional search marketing, search engine optimization, and display advertising services. We generate consumer project inquiries (&quot;Leads&quot;) from homeowners looking for specific residential contractors and deliver these details exclusively to registered, qualified clients who serve the relevant geographic territory.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Exclusive Lead Delivery</h3>
              <p>We guarantee that all leads delivered under our standard &quot;Exclusive Campaign&quot; package are routed strictly to one client company. We do not resell, share, or broadcast identical homeowner details to any third-party contractors once designated to you, ensuring a highly credible B2B interaction framework.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Contractor Licensing & Compliance</h3>
              <p>By purchasing leads, you represent and warrant that you are a properly licensed, bonded, and insured contractor in good standing in your operating state and local municipal jurisdictions. Precision Home Leads LLC assumes zero liability for your workmanship, regulatory permits, or eventual residential project contracts finalized with consumers.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">4. Limitation of Liability & Commercial Claims</h3>
              <p>Under no circumstances shall Precision Home Leads LLC, its members, directors, or representatives be liable for any indirect, consequential, or incidental business losses, lost project revenues, or disputes arising between you and any homeowner. Our entire corporate liability is strictly capped at the direct amount paid by you during the preceding 30-day service window.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">5. Dispute Resolution & Governing Law</h3>
              <p>Any commercial dispute, claim, or breach arising from this agreement shall be governed by and construed in accordance with the laws of the State of Illinois, USA, without regard to conflict of law principles. Both parties consent to personal jurisdiction and exclusive venue in the state or federal courts located in DeKalb County, Illinois.</p>
            </div>
          )
        };
      
      case "/terms-conditions":
        return {
          title: "Terms & Conditions",
          effectiveDate: "November 12, 2025",
          subtitle: "Commercial Framework & Service Level Agreements",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>These commercial Terms & Conditions govern all transactions, billing frameworks, and service level agreements (SLAs) between Precision Home Leads LLC and our corporate clients.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Billing Accounts & Deposit Fees</h3>
              <p>To receive live qualified leads, clients must maintain an active corporate account linked to a valid credit card or banking routing file. Account deposits and lead packages are billed in advance. All charges are detailed on transparent invoices compliant with merchant card standards.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Real-Time Delivery SLAs</h3>
              <p>Precision Home Leads LLC strives to deliver all validated homeowner inquiries within five (5) minutes of digital verification. Lead delivery notifications are dispatched via SMS to your configured mobile devices and complete text dossiers via email. System delays from telecom carriers or local internet issues are beyond our reasonable scope of control.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Chargebacks and Underwriting Protections</h3>
              <p>We maintain meticulous logs of all lead deliveries, including timestamp data, SMS receipt responses, and verified emails to satisfy merchant payment carrier underwriting (Visa, Mastercard, Stripe, banks). Raising false chargebacks or fraudulent disputes violates this agreement and may lead to immediate account termination, collection action, and credit bureau reporting.</p>
            </div>
          )
        };

      case "/privacy":
        return {
          title: "Privacy Policy",
          effectiveDate: "November 12, 2025",
          subtitle: "How We Handle Your Information",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>Precision Home Leads LLC committed to protecting your privacy. This policy describes how we collect, store, share, and safeguard information submitted by contractors and homeowners through our digital portals.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Information We Collect</h3>
              <p>We collect personal information including full name, business email, telephone numbers, contractor company names, target specialty trades, and project zip codes. This is gathered only when voluntarily submitted via our secure lead forms, scheduling widgets, or direct email inquiries.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Secure Storage & Data Encryption</h3>
              <p>All data is transmitted securely utilizing standard SSL/TLS (Secure Sockets Layer) 256-bit encryption protocol. We store information in firewalled cloud environments with strict user authorization boundaries to prevent unauthorized modification or data leaks.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Consumer Data Sharing Disclosures</h3>
              <p>Precision Home Leads LLC does not rent, sell, or trade personal data to third-party list brokers. Homeowner lead information is shared strictly with the one local licensed home service contractor purchased to fulfill that specific geographical project trade. No other corporate sharing takes place.</p>
            </div>
          )
        };

      case "/consumer-privacy":
        return {
          title: "Consumer Privacy Notice",
          effectiveDate: "November 12, 2025",
          subtitle: "State-Specific Rights & Federal Notices",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>This Consumer Privacy Notice provides details on consumer rights under the California Consumer Privacy Act (CCPA) and federal privacy standards.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Right to Know & Access</h3>
              <p>Consumers have the absolute right to request details regarding the categories and specific pieces of personal information Precision Home Leads LLC has captured, saved, or shared during the preceding 12 months.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Right to Delete</h3>
              <p>You can request that we permanently delete your contact file and project coordinates from our databases. To initiate a delete request, email your request to contact@precisionleads.co with the subject &quot;CCPA Delete Request.&quot;</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Non-Discrimination Statement</h3>
              <p>We will never discriminate against you, offer lower quality service, or charge different commercial rates because you exercised your consumer privacy rights under federal or state guidelines.</p>
            </div>
          )
        };

      case "/refund-policy":
        return {
          title: "Refund Policy",
          effectiveDate: "November 12, 2025",
          subtitle: "Our Case-by-Case Lead Replacement Process",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed" data-testid="refund-policy-content">
              <p>Precision Home Leads LLC provides specialized digital marketing, search placement, and B2B homeowner lead generation services. Because we produce real-time, exclusive digital assets and spend operational capital on paid display placement, refunds and lead replacement requests are reviewed on a case-by-case basis according to company policy.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Lead Replacement and Disputes</h3>
              <p>If you receive an invalid lead, you may submit a replacement request through our online support desk or email contact@precisionleads.co. A lead is considered eligible for replacement review if it meets any of the following criteria:</p>
              <ul className="list-disc pl-5 space-y-2 text-xs">
                <li><strong>Unresponsive / No Answer:</strong> You made documented, good-faith follow-up attempts to reach the homeowner over a period of four (4) calendar days and received no answer or response.</li>
                <li><strong>Already Under Contract:</strong> The homeowner has already signed or committed to a contract with another company for the requested project.</li>
                <li><strong>Service Not Offered:</strong> The homeowner requested a service or trade that your business does not offer or that falls outside your contracted campaign specialty.</li>
                <li><strong>No Longer Interested:</strong> At the time of first contact, the homeowner states they are no longer interested in the project or have cancelled their plans.</li>
                <li>Disconnected or wrong phone number (e.g. fax line, or a business office instead of the homeowner).</li>
                <li>The property is located outside of your contracted geographic territory parameters.</li>
                <li>The inquiry contains clearly fabricated details (e.g. Name: &apos;Fake Name&apos;).</li>
              </ul>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Review Process</h3>
              <p>Each replacement dispute is reviewed by your dedicated corporate account manager. For unresponsive leads, we ask that you document your follow-up attempts across the four (4) day window; we also test dial phone numbers and cross-reference email and delivery logs. If a lead is confirmed eligible under the criteria above, we will credit your account with a replacement lead at no additional charge, typically within two (2) business days. Approved cases are resolved through lead replacement; monetary refunds, where applicable, remain at the company&apos;s discretion on a case-by-case basis.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Proactive Account Audits</h3>
              <p>We are committed to absolute billing fairness. In the event of system outages or delivery discrepancies, our accounting team performs retroactive account audits to ensure your pre-paid balances reflect actual delivered value, satisfying bank card underwriting safety standards.</p>
            </div>
          )
        };

      case "/cancellation-policy":
        return {
          title: "Cancellation Policy",
          effectiveDate: "November 12, 2025",
          subtitle: "Stopping Future Services & Campaign Pauses",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed" data-testid="cancellation-policy-content">
              <p>This Cancellation Policy explains how corporate clients may pause, modify, or permanently stop future digital marketing campaigns and lead deliveries with Precision Home Leads LLC.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. No Long-Term Lock-Ins</h3>
              <p>Clients are never subject to multi-year contracts. You can pause or cancel future lead delivery services at any time. Cancellation requests can be submitted by contacting your account strategist or emailing contact@precisionleads.co.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Non-Refundability of Delivered Assets</h3>
              <p>While you may stop future deliveries, services already performed, active display campaigns currently running, or homeowner leads already delivered to your portal prior to cancellation are generally not refundable and must be paid in full.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Final Account Settlement</h3>
              <p>Upon receiving a cancellation notice, we will immediately pause your digital marketing ad channels. Any remaining unallocated pre-paid credit balances will be verified, audited, and returned to your funding card on file according to standard bank processing timelines (typically 5-10 business days).</p>
            </div>
          )
        };

      case "/sms-terms":
        return {
          title: "SMS Terms & Conditions",
          effectiveDate: "November 12, 2025",
          subtitle: "Standard Carrier Disclosures & TCPA Compliance",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed" data-testid="sms-policy-content">
              <p>By subscribing to our notifications, you agree to receive automated SMS alerts and text messages from Precision Home Leads LLC.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Opt-In & Message Content</h3>
              <p>Text notifications are utilized strictly to deliver real-time homeowner leads, account billing reminders, or critical onboarding status alerts. Consent to receive SMS messages is verified during account onboarding.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Standard Stop & Help Protocols</h3>
              <p>We respect consumer and contractor choice. You may cancel SMS notifications at any time:</p>
              <ul className="list-disc pl-5 space-y-2 text-xs">
                <li>To opt-out and stop receiving text messages, reply with the keyword <strong>STOP</strong> to our shortcode or sending phone number. You will receive one final text confirming your opt-out status.</li>
                <li>For carrier support details or instructions, reply with the keyword <strong>HELP</strong> to receive direct contact instructions.</li>
              </ul>

              <h3 className="text-base font-bold text-slate-900 pt-4">3. Carrier Rates & Disclaimers</h3>
              <p>Precision Home Leads LLC does not levy fees for SMS notifications. However, standard message and data rates may apply from your mobile carrier. Message frequency varies based on your active campaign geographic lead volume.</p>
            </div>
          )
        };

      case "/cookie-policy":
        return {
          title: "Cookie Policy",
          effectiveDate: "November 12, 2025",
          subtitle: "Tracking Technologies & Analytics",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>Precision Home Leads LLC uses cookies, tracking pixels, and local storage on our websites to optimize visitor experiences and track performance metrics.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Why We Use Cookies</h3>
              <p>We use essential cookies to maintain secure sessions, remember form inputs, and track how visitors interact with our content. We also use analytics cookies (such as Google Analytics) to measure digital ad ROI and website traffic volume.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Opt-Out Options</h3>
              <p>You can configure your browser to block or alert you about cookies, but some parts of our site may not function properly. By using our website, you consent to tracking technologies in accordance with this policy.</p>
            </div>
          )
        };

      case "/accessibility":
        return {
          title: "Accessibility Statement",
          effectiveDate: "November 12, 2025",
          subtitle: "Our Commitment to Digital Inclusion",
          content: (
            <div className="space-y-6 text-sm text-slate-600 leading-relaxed">
              <p>Precision Home Leads LLC is dedicated to ensuring our digital assets are accessible and usable for everyone, regardless of sensory ability or device technology.</p>
              
              <h3 className="text-base font-bold text-slate-900 pt-4">1. Conformance Standards</h3>
              <p>We proactively audit and update our website structures to conform with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA criteria, which is the benchmark standard for ADA digital accessibility.</p>

              <h3 className="text-base font-bold text-slate-900 pt-4">2. Feedback and Assistance</h3>
              <p>If you experience any visual, auditory, or cognitive obstacles while navigating our pages, please let us know. You can email us at contact@precisionleads.co. We review accessibility barriers and resolve them promptly.</p>
            </div>
          )
        };

      default:
        return {
          title: "Legal Information Center",
          effectiveDate: "November 12, 2025",
          subtitle: "Precision Home Leads LLC",
          content: <p>Please select a specific legal link from the footer to review detailed policies, terms, and compliance disclosures.</p>
        };
    }
  };

  const page = getPageData();

  return (
    <div className="bg-slate-50 min-h-screen py-16 md:py-24" data-testid="legal-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Compliance Header */}
        <div className="bg-white border border-slate-200 rounded-t-lg p-8 md:p-12 space-y-4 border-b-0">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-emerald-600" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Merchant Risk & Compliance Disclosure</span>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="legal-title-header">
            {page.title}
          </h1>
          <p className="text-xs text-slate-400">
            Effective Date: {page.effectiveDate} | Corporate Underwriting Status: <span className="text-emerald-600 font-semibold uppercase tracking-wider">VERIFIED</span>
          </p>
          <div className="border-b border-slate-100 pt-4" />
          <p className="text-sm font-semibold text-slate-800 italic">
            {page.subtitle}
          </p>
        </div>

        {/* Content Body */}
        <div className="bg-white border border-slate-200 rounded-b-lg p-8 md:p-12 shadow-sm">
          {page.content}
        </div>

        {/* Security Assurances */}
        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400 px-2">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-4.5 w-4.5 text-emerald-600" />
            256-Bit SSL Secured Document
          </div>
          <p>Precision Home Leads LLC • Legal Department</p>
        </div>

      </div>
    </div>
  );
}
